/* eslint-disable */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
 AreaChart, Area, BarChart, Bar, XAxis, YAxis, Tooltip,
 ResponsiveContainer, CartesianGrid, Cell, RadarChart, Radar,
 PolarGrid, PolarAngleAxis,
} from 'recharts';
// ─── API ─────────────────────────────────────────────────────────────────────
const api = async (sti) => {
 const r = await fetch('/api' + sti, {
 credentials: 'include',
 });
 if (!r.ok) throw new Error(`HTTP ${r.status}`);
 return r.json();
};
// ─── Design tokens ────────────────────────────────────────────────────────────
const BG = '#0a0e1a';
const BG2 = '#0f172a';
const BG_CARD = 'rgba(255,255,255,0.04)';
const BG_CARD2 = 'rgba(255,255,255,0.07)';
const BORDER = 'rgba(255,255,255,0.08)';
const PRIMARY = '#2322be';
const ACCENT = '#3fd4c7';
const GOLD = '#c9a84c';
const OK = '#22c55e';
const WARN = '#f59e0b';
const DANGER = '#ef4444';
const TEXT = '#f1f5f9';
const SUBTEXT = '#94a3b8';
const FONT = "'Inter', system-ui, sans-serif";
const DIVISION_IKONER = {
 1:'',2:'',3:'',4:'',5:'',6:'',7:'',8:'',9:'',10:'',11:'',12:'',13:'',
};
function statusFarve(s) {
 return s==='ok' ? OK : s==='advarsel' ? WARN : s==='kritisk' ? DANGER : SUBTEXT;
}
// ─── Countup animation ────────────────────────────────────────────────────────
function Countup({ til, varighed = 1200, prefix = '', suffix = '', decimaler = 0 }) {
 const [val, setVal] = useState(0);
 const ref = useRef(null);
 useEffect(() => {
 let start = null;
 const animate = (ts) => {
 if (!start) start = ts;
 const pct = Math.min((ts - start) / varighed, 1);
 const ease = 1 - Math.pow(1 - pct, 3);
 setVal(parseFloat((til * ease).toFixed(decimaler)));
 if (pct < 1) ref.current = requestAnimationFrame(animate);
 };
 ref.current = requestAnimationFrame(animate);
 return () => cancelAnimationFrame(ref.current);
 }, [til, varighed, decimaler]);
 return <span>{prefix}{decimaler > 0 ? val.toFixed(decimaler) : Math.round(val).toLocaleString('da-DK')}{suffix}</span>;
}
// ─── Header (fælles for alle slides) ─────────────────────────────────────────
function Header({ nu, driftNiveau, driftAarsager, slideIdx, totalSlides, slideTimer, SLIDE_SEC }) {
 const dato = nu.toLocaleDateString('da-DK', { weekday:'long', day:'numeric', month:'long' });
 const klok = nu.toLocaleTimeString('da-DK', { hour:'2-digit', minute:'2-digit', second:'2-digit' });
 const progressPct = ((SLIDE_SEC - slideTimer) / SLIDE_SEC) * 100;
 // Tre tilstande: ok (grøn), advarsel (gul), kritisk (rød)
 const niveau = driftNiveau || 'ok';
 const dotFarve = niveau === 'kritisk' ? DANGER : niveau === 'advarsel' ? WARN : OK;
 const statusTekst =
 niveau === 'kritisk' ? 'System: kritisk — handling påkrævet' :
 niveau === 'advarsel' ? `System: ${driftAarsager.length} advarsel${driftAarsager.length !== 1 ? 'er' : ''}` :
 'Alle systemer OK';
 return (
 <div style={{ marginBottom: 28 }}> <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:12 }}>
 {/* Venstre: brand */}
 <div style={{ display:'flex', alignItems:'center', gap:16 }}> <div style={{
 width:48, height:48, borderRadius:14,
 background:`linear-gradient(135deg, ${PRIMARY}, ${ACCENT})`,
 display:'flex', alignItems:'center', justifyContent:'center',
 fontSize:22, fontWeight:800, color:'#fff',
 }}>SV</div> <div> <div style={{ fontSize:'clamp(18px,1.5vw,28px)', fontWeight:700, color:TEXT, letterSpacing:'-0.02em' }}>
 SkilsmisseVejen
 </div> <div style={{ fontSize:'clamp(11px,0.9vw,15px)', color:ACCENT, textTransform:'capitalize' }}>{dato}</div> </div> </div>
 {/* Midten: slide indikatorer */}
 <div style={{ display:'flex', gap:8, alignItems:'center' }}>
 {Array.from({ length: totalSlides }).map((_, i) => (
 <div key={i} style={{
 width: i === slideIdx ? 24 : 8, height:8, borderRadius:4,
 background: i === slideIdx ? ACCENT : 'rgba(255,255,255,0.2)',
 transition:'all .3s',
 }} />
 ))}
 </div>
 {/* Højre: klokke + systemstatus */}
 <div style={{ textAlign:'right' }}> <div style={{ fontSize:'clamp(32px,3.5vw,64px)', fontWeight:200, color:TEXT, fontVariantNumeric:'tabular-nums', lineHeight:1 }}>
 {klok.slice(0,5)}<span style={{ color:ACCENT, fontSize:'0.6em' }}>:{klok.slice(6)}</span> </div> <div style={{ fontSize:'clamp(10px,0.8vw,13px)', color:dotFarve, marginTop:4, display:'flex', alignItems:'center', gap:6, justifyContent:'flex-end' }}> <span style={{
 width:8, height:8, borderRadius:'50%', display:'inline-block',
 background: dotFarve,
 boxShadow: `0 0 8px ${dotFarve}`,
 }} />
 {statusTekst}
 </div> </div> </div>
 {/* Progress bar */}
 <div style={{ height:2, background:'rgba(255,255,255,0.06)', borderRadius:1, overflow:'hidden' }}> <motion.div
 style={{ height:'100%', background:`linear-gradient(90deg, ${PRIMARY}, ${ACCENT})`, borderRadius:1 }}
 initial={{ width:'0%' }}
 animate={{ width:`${progressPct}%` }}
 transition={{ duration:1, ease:'linear' }}
 /> </div> </div>
 );
}
// ─── SLIDE 1: Virksomhedspuls ─────────────────────────────────────────────────
function SlideVirksomhedspuls({ data }) {
 const m = data?.metrikker || {};
 const mrr = parseFloat(m.mrr?.vaerdi || 0);
 const mrrMaal = parseFloat(m.mrr?.maal || 292000);
 const klienter = parseFloat(m.aktive_klienter?.vaerdi || 0) || parseFloat(data?.sager?.aktive_sager || 0);
 const leads = parseFloat(m.nye_leads_maaned?.vaerdi || 0);
 const fb = parseFloat(m.fb_gruppe_medlemmer?.vaerdi || 3000);
 const runway = parseFloat(m.runway_maaneder?.vaerdi || 0);
 const oppetid = parseFloat(m.api_oppetid?.vaerdi || 99.5);
 const lonObl = 60000;
 const realistiskMaal = 3750000;
 const mrrPct = Math.min(100, Math.round((mrr / (realistiskMaal / 12)) * 100));
 const scenarier = [
 { navn:'Konservativt', maal:2500000, farve:'#475569' },
 { navn:'Realistisk', maal:3750000, farve:ACCENT },
 { navn:'Ambitiøst', maal:5500000, farve:GOLD },
 ];
 const metrics = [
 { label:'MRR', value:mrr, suffix:' kr', farve:ACCENT, stor:true },
 { label:'Aktive klienter', value:klienter, suffix:'', farve:'#60a5fa', stor:true },
 { label:'Nye leads/md', value:leads, suffix:'', farve:OK, stor:true },
 { label:'FB gruppe', value:fb, suffix:' mbr', farve:'#a78bfa', stor:false },
 { label:'Runway', value:runway, suffix:' md', farve:WARN, stor:false },
 { label:'API oppetid', value:oppetid, suffix:'%', farve:oppetid >= 99.5 ? OK : DANGER, stor:false, decimaler:1 },
 ];
 return (
 <div> <div style={{ display:'flex', alignItems:'center', gap:12, marginBottom:24 }}> <span style={{ fontSize:28 }}></span> <h2 style={{ margin:0, fontSize:'clamp(20px,1.8vw,32px)', fontWeight:800, color:TEXT }}>Virksomhedspuls</h2> <div style={{
 marginLeft:'auto', background:'rgba(34,197,94,0.12)', border:`1px solid rgba(34,197,94,0.3)`,
 borderRadius:20, padding:'4px 14px', fontSize:13, color:OK, fontWeight:600,
 }}>
 Lønforpligtelse: {lonObl.toLocaleString('da-DK')} kr/md 
 </div> </div>
 {/* 6 metrik-kort */}
 <div className="sv-infoskaerm-kpi" style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:16, marginBottom:24 }}>
 {metrics.map((m2, i) => (
 <motion.div key={i}
 initial={{ opacity:0, y:20 }}
 animate={{ opacity:1, y:0 }}
 transition={{ delay: i * 0.07 }}
 style={{
 background: BG_CARD2, border:`1px solid ${BORDER}`, borderRadius:14,
 padding: m2.stor ? '20px 24px' : '16px 20px',
 borderTop:`2px solid ${m2.farve}`,
 }}
 > <div style={{ fontSize:12, color:SUBTEXT, marginBottom:8, letterSpacing:'0.05em', textTransform:'uppercase', fontWeight:600 }}>
 {m2.label}
 </div> <div style={{ fontSize: m2.stor ? 'clamp(28px,2.5vw,48px)' : 'clamp(22px,2vw,36px)', fontWeight:800, color:m2.farve, lineHeight:1 }}> <Countup til={m2.value} decimaler={m2.decimaler || 0} suffix={m2.suffix} varighed={800} /> </div> </motion.div>
 ))}
 </div>
 {/* Revenue scenario */}
 <div style={{ background:BG_CARD, border:`1px solid ${BORDER}`, borderRadius:14, padding:'20px 24px' }}> <div style={{ fontSize:13, color:SUBTEXT, marginBottom:14, fontWeight:600, textTransform:'uppercase', letterSpacing:'0.05em' }}>
 Omsætningscenarier — årsplan
 </div>
 {scenarier.map((s, i) => {
 const pct = Math.min(100, Math.round((mrr * 12 / s.maal) * 100));
 return (
 <div key={i} style={{ marginBottom: i < scenarier.length - 1 ? 12 : 0 }}> <div style={{ display:'flex', justifyContent:'space-between', marginBottom:6 }}> <span style={{ fontSize:13, color:TEXT, fontWeight:500 }}>{s.navn}</span> <span style={{ fontSize:13, color:s.farve, fontWeight:700 }}>
 {(s.maal / 1000000).toFixed(1)}M kr — {pct}% nået
 </span> </div> <div style={{ height:8, background:'rgba(255,255,255,0.06)', borderRadius:4, overflow:'hidden' }}> <motion.div
 initial={{ width:0 }}
 animate={{ width:`${pct}%` }}
 transition={{ duration:1.2, delay: i * 0.2 }}
 style={{ height:'100%', background:s.farve, borderRadius:4 }}
 /> </div> </div>
 );
 })}
 </div> </div>
 );
}
// ─── SLIDE 2: Division Sundhed ─────────────────────────────────────────────────
function SlideDivisionSundhed({ data }) {
 const divisioner = data?.divisioner || [];
 const total = divisioner.length;
 const grønne = divisioner.filter(d => parseInt(d.kpi_kritisk||0) === 0 && parseInt(d.kpi_advarsel||0) === 0 && parseInt(d.kpi_ok||0) > 0).length;
 const advarsler = divisioner.filter(d => parseInt(d.kpi_advarsel||0) > 0 && parseInt(d.kpi_kritisk||0) === 0).length;
 const kritiske = divisioner.filter(d => parseInt(d.kpi_kritisk||0) > 0).length;
 const ukendte = total - grønne - advarsler - kritiske;
 // Brug drift_status fra backend til operationel health score
 const driftStatus = data?.drift_status;
 const driftNiveau = driftStatus?.niveau || 'ok';
 const driftAarsager = driftStatus?.aarsager || [];
 return (
 <div> <div style={{ display:'flex', alignItems:'center', gap:12, marginBottom:20 }}> <span style={{ fontSize:28 }}></span> <h2 style={{ margin:0, fontSize:'clamp(20px,1.8vw,32px)', fontWeight:800, color:TEXT }}>Division Sundhed</h2> <div style={{ marginLeft:'auto', display:'flex', gap:16, fontSize:13 }}>
 {grønne > 0 && <span style={{ color:OK }}> {grønne} grøn</span>}
 {advarsler > 0 && <span style={{ color:WARN }}> {advarsler} advarsel</span>}
 {kritiske > 0 && <span style={{ color:DANGER }}>× {kritiske} kritisk</span>}
 {ukendte > 0 && <span style={{ color:SUBTEXT }}>○ {ukendte} ikke målt</span>}
 </div> </div>
 {/* Operationel health score — baseret på faktiske driftssignaler */}
 <div style={{
 background: driftNiveau === 'kritisk' ? 'rgba(239,68,68,0.08)' : driftNiveau === 'advarsel' ? 'rgba(245,158,11,0.08)' : 'rgba(34,197,94,0.08)',
 border: `1px solid ${driftNiveau === 'kritisk' ? 'rgba(239,68,68,0.3)' : driftNiveau === 'advarsel' ? 'rgba(245,158,11,0.3)' : 'rgba(34,197,94,0.3)'}`,
 borderRadius:12, padding:'14px 20px', marginBottom:20,
 }}>
 {driftNiveau === 'ok' ? (
 <span style={{ fontSize:'clamp(16px,1.4vw,22px)', fontWeight:700, color:OK }}>
 Alle systemer grønne — firmaet kører optimalt
 </span>
 ) : (
 <div> <span style={{ fontSize:'clamp(14px,1.2vw,20px)', fontWeight:700, color: driftNiveau === 'kritisk' ? DANGER : WARN, display:'block', marginBottom: driftAarsager.length > 0 ? 8 : 0 }}>
 {driftNiveau === 'kritisk' ? 'System kritisk — handling påkrævet' : 'System: advarsel'}
 </span>
 {driftAarsager.length > 0 && (
 <ul style={{ margin:0, paddingLeft:18, fontSize:'clamp(12px,0.95vw,15px)', color: driftNiveau === 'kritisk' ? DANGER : WARN }}>
 {driftAarsager.map((a, i) => <li key={i}>{a}</li>)}
 </ul>
 )}
 </div>
 )}
 {ukendte > 0 && (
 <div style={{ fontSize:'clamp(11px,0.85vw,13px)', color:SUBTEXT, marginTop:8 }}>
 {ukendte} afdeling{ukendte !== 1 ? 'er' : ''} med KPI-status: ikke målt
 </div>
 )}
 </div>
 {/* Division grid */}
 <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(clamp(140px,12vw,200px),1fr))', gap:10 }}>
 {divisioner.map((div, i) => {
 const ok = parseInt(div.kpi_ok||0);
 const adv = parseInt(div.kpi_advarsel||0);
 const kri = parseInt(div.kpi_kritisk||0);
 const status = kri > 0 ? 'kritisk' : adv > 0 ? 'advarsel' : ok > 0 ? 'ok' : 'ukendt';
 const farve = statusFarve(status);
 return (
 <motion.div key={div.division_nr}
 initial={{ opacity:0, scale:0.9 }}
 animate={{ opacity:1, scale:1 }}
 transition={{ delay: i * 0.04 }}
 style={{
 background: BG_CARD2, border:`1px solid ${BORDER}`,
 borderRadius:12, padding:'14px 16px',
 borderLeft:`4px solid ${farve}`,
 }}
 > <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:8 }}> <span style={{ fontSize:18 }}>{DIVISION_IKONER[div.division_nr]}</span> <div> <div style={{ fontSize:10, color:SUBTEXT, fontWeight:600 }}>DIV {div.division_nr}</div> <div style={{ fontSize:12, fontWeight:700, color:TEXT, lineHeight:1.2 }}>{div.division_navn}</div> </div> </div> <div style={{ display:'flex', gap:6, flexWrap:'wrap' }}> <span style={{ fontSize:11, color:SUBTEXT }}>{div.antal_afd} afd.</span>
 {ok > 0 && <span style={{ fontSize:11, color:OK, fontWeight:600 }}>{ok}</span>}
 {adv > 0 && <span style={{ fontSize:11, color:WARN, fontWeight:600 }}>{adv}</span>}
 {kri > 0 && <span style={{ fontSize:11, color:DANGER, fontWeight:600 }}>×{kri}</span>}
 </div> <div style={{
 marginTop:10, height:4, background:'rgba(255,255,255,0.06)', borderRadius:2, overflow:'hidden',
 }}> <div style={{ height:'100%', width:`${ok+adv+kri > 0 ? Math.round((ok/(ok+adv+kri))*100) : 0}%`, background:farve, borderRadius:2, transition:'width 1s' }} /> </div> </motion.div>
 );
 })}
 </div> </div>
 );
}
// ─── SLIDE 3: Leads Pipeline ──────────────────────────────────────────────────
function SlideLeadsPipeline({ data }) {
 const leads = data?.leads || [];
 const statusser = ['ny','kontaktet','møde','klient'];
 const statusLabels = { ny:'Nye leads', kontaktet:'Kontaktet', møde:'Møde booket', klient:'Konverteret' };
 const statusFarver = { ny:'#60a5fa', kontaktet:'#a78bfa', møde:GOLD, klient:OK };
 const kilder = [...new Set(leads.map(l => l.kilde))];
 const chart_data = statusser.map(s => ({
 name: statusLabels[s],
 ...Object.fromEntries(kilder.map(k => [k, leads.find(l => l.kilde === k && l.status === s)?.antal || 0])),
 }));
 const totalt_nye = leads.filter(l => l.status === 'ny').reduce((s, l) => s + (l.antal||0), 0);
 const totalt_klient = leads.filter(l => l.status === 'klient').reduce((s, l) => s + (l.antal||0), 0);
 const konvRate = totalt_nye > 0 ? Math.round((totalt_klient / totalt_nye) * 100) : 0;
 const KILDE_FARVER = ['#60a5fa','#3fd4c7','#f59e0b','#a78bfa','#22c55e','#ef4444'];
 return (
 <div> <div style={{ display:'flex', alignItems:'center', gap:12, marginBottom:20 }}> <span style={{ fontSize:28 }}></span> <h2 style={{ margin:0, fontSize:'clamp(20px,1.8vw,32px)', fontWeight:800, color:TEXT }}>
 Leads Pipeline — {new Date().toLocaleString('da-DK',{ month:'long', year:'numeric' })}
 </h2> </div>
 {/* Summary */}
 <div className="sv-infoskaerm-kpi" style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:16, marginBottom:24 }}>
 {[
 { label:'Nye leads', value:totalt_nye, farve:'#60a5fa' },
 { label:'Konverterede', value:totalt_klient, farve:OK },
 { label:'Konverteringsrate', value:konvRate, suffix:'%', farve:konvRate >= 10 ? OK : konvRate >= 5 ? WARN : DANGER },
 ].map((s, i) => (
 <motion.div key={i}
 initial={{ opacity:0, y:15 }}
 animate={{ opacity:1, y:0 }}
 transition={{ delay: i * 0.1 }}
 style={{ background:BG_CARD2, border:`1px solid ${BORDER}`, borderRadius:12, padding:'16px 20px', textAlign:'center' }}
 > <div style={{ fontSize:11, color:SUBTEXT, marginBottom:6, textTransform:'uppercase', letterSpacing:'0.05em' }}>{s.label}</div> <div style={{ fontSize:'clamp(32px,3vw,56px)', fontWeight:800, color:s.farve }}> <Countup til={s.value} suffix={s.suffix||''} varighed={1000} /> </div> </motion.div>
 ))}
 </div>
 {/* Bar chart */}
 {chart_data.length > 0 && kilder.length > 0 && (
 <div style={{ background:BG_CARD, border:`1px solid ${BORDER}`, borderRadius:14, padding:'20px 24px' }}> <div style={{ fontSize:12, color:SUBTEXT, marginBottom:14, textTransform:'uppercase', letterSpacing:'0.05em', fontWeight:600 }}>
 Leads per kilde og fase
 </div> <div style={{ display:'flex', gap:12, flexWrap:'wrap', marginBottom:12 }}>
 {kilder.map((k, i) => (
 <div key={k} style={{ display:'flex', alignItems:'center', gap:6 }}> <div style={{ width:10, height:10, borderRadius:2, background:KILDE_FARVER[i % KILDE_FARVER.length] }} /> <span style={{ fontSize:12, color:SUBTEXT }}>{k}</span> </div>
 ))}
 </div> <ResponsiveContainer width="100%" height={160}> <BarChart data={chart_data} margin={{ top:0, right:0, left:-20, bottom:0 }}> <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.04)" /> <XAxis dataKey="name" tick={{ fill:SUBTEXT, fontSize:12 }} axisLine={false} tickLine={false} /> <YAxis tick={{ fill:SUBTEXT, fontSize:11 }} axisLine={false} tickLine={false} /> <Tooltip contentStyle={{ background:BG2, border:`1px solid ${BORDER}`, borderRadius:8, color:TEXT }} />
 {kilder.map((k, i) => (
 <Bar key={k} dataKey={k} fill={KILDE_FARVER[i % KILDE_FARVER.length]} radius={[4,4,0,0]} />
 ))}
 </BarChart> </ResponsiveContainer> </div>
 )}
 </div>
 );
}
// ─── SLIDE 4: Sagsportal Live ──────────────────────────────────────────────────
function SlideSagsportal({ data, trendData }) {
 const sager = data?.sager || {};
 const os = data?.os_stat || {};
 const aktive = parseInt(sager.aktive_sager || 0);
 const nye_md = parseInt(sager.nye_sager_md || 0);
 const fuldfoert = parseInt(os.fuldfoert_24h || 0);
 const afventer = parseInt(os.afventer_beslutninger || 0);
 const trend = (trendData?.trend || []).slice(-12).map(t => ({
 uge: t.uge?.slice(5) || '',
 klienter: t.aktive_klienter || 0,
 mrr: Math.round((t.mrr || 0) / 1000),
 }));
 return (
 <div> <div style={{ display:'flex', alignItems:'center', gap:12, marginBottom:20 }}> <span style={{ fontSize:28 }}></span> <h2 style={{ margin:0, fontSize:'clamp(20px,1.8vw,32px)', fontWeight:800, color:TEXT }}>Sagsportal & OS Live</h2> <div style={{
 marginLeft:'auto', fontSize:12, color:SUBTEXT,
 display:'flex', alignItems:'center', gap:6,
 }}> <span style={{ width:8, height:8, borderRadius:'50%', background:OK, display:'inline-block', boxShadow:`0 0 8px ${OK}` }} />
 Live data
 </div> </div> <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:20, marginBottom:24 }}>
 {/* Sagsportal */}
 <div style={{ background:BG_CARD2, border:`1px solid ${BORDER}`, borderRadius:14, padding:'20px 24px' }}> <div style={{ fontSize:12, color:SUBTEXT, marginBottom:16, textTransform:'uppercase', letterSpacing:'0.05em', fontWeight:600 }}>Sagsportal</div> <div style={{ display:'flex', gap:20 }}> <div> <div style={{ fontSize:'clamp(40px,3.5vw,72px)', fontWeight:800, color:ACCENT, lineHeight:1 }}> <Countup til={aktive} varighed={800} /> </div> <div style={{ fontSize:12, color:SUBTEXT, marginTop:4 }}>Aktive sager</div> </div> <div style={{ borderLeft:`1px solid ${BORDER}`, paddingLeft:20 }}> <div style={{ fontSize:'clamp(28px,2.5vw,48px)', fontWeight:800, color:OK, lineHeight:1 }}> <Countup til={nye_md} varighed={800} /> </div> <div style={{ fontSize:12, color:SUBTEXT, marginTop:4 }}>Nye denne måned</div> </div> </div> </div>
 {/* OS status */}
 <div style={{ background:BG_CARD2, border:`1px solid ${BORDER}`, borderRadius:14, padding:'20px 24px' }}> <div style={{ fontSize:12, color:SUBTEXT, marginBottom:16, textTransform:'uppercase', letterSpacing:'0.05em', fontWeight:600 }}>OS Aktivitet</div> <div style={{ display:'flex', gap:20 }}> <div> <div style={{ fontSize:'clamp(40px,3.5vw,72px)', fontWeight:800, color:OK, lineHeight:1 }}> <Countup til={fuldfoert} varighed={800} /> </div> <div style={{ fontSize:12, color:SUBTEXT, marginTop:4 }}>Opgaver i dag</div> </div> <div style={{ borderLeft:`1px solid ${BORDER}`, paddingLeft:20 }}> <div style={{ fontSize:'clamp(28px,2.5vw,48px)', fontWeight:800, color: afventer > 0 ? WARN : OK, lineHeight:1 }}> <Countup til={afventer} varighed={800} /> </div> <div style={{ fontSize:12, color:SUBTEXT, marginTop:4 }}>Afventer beslutning</div> </div> </div> </div> </div>
 {/* Trend chart */}
 {trend.length > 2 && (
 <div style={{ background:BG_CARD, border:`1px solid ${BORDER}`, borderRadius:14, padding:'20px 24px' }}> <div style={{ fontSize:12, color:SUBTEXT, marginBottom:14, textTransform:'uppercase', letterSpacing:'0.05em', fontWeight:600 }}>
 MRR trend — seneste 12 uger (×1000 kr)
 </div> <ResponsiveContainer width="100%" height={150}> <AreaChart data={trend} margin={{ top:0, right:0, left:-20, bottom:0 }}> <defs> <linearGradient id="mrrGrad" x1="0" y1="0" x2="0" y2="1"> <stop offset="5%" stopColor={ACCENT} stopOpacity={0.25} /> <stop offset="95%" stopColor={ACCENT} stopOpacity={0} /> </linearGradient> </defs> <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.04)" /> <XAxis dataKey="uge" tick={{ fill:SUBTEXT, fontSize:11 }} axisLine={false} tickLine={false} /> <YAxis tick={{ fill:SUBTEXT, fontSize:11 }} axisLine={false} tickLine={false} /> <Tooltip
 contentStyle={{ background:BG2, border:`1px solid ${BORDER}`, borderRadius:8, color:TEXT }}
 formatter={(v) => [`${v}k kr`, 'MRR']}
 /> <Area type="monotone" dataKey="mrr" stroke={ACCENT} fill="url(#mrrGrad)" strokeWidth={2} dot={false} /> </AreaChart> </ResponsiveContainer> </div>
 )}
 </div>
 );
}
// ─── SLIDE 5: KPI Status / Alt OK ─────────────────────────────────────────────
function SlideKpiStatus({ data }) {
 const kritiske = data?.kpi_kritiske || [];
 const altOk = kritiske.length === 0;
 return (
 <div style={{ display:'flex', flexDirection:'column' }}> <div style={{ display:'flex', alignItems:'center', gap:12, marginBottom:20 }}> <span style={{ fontSize:28 }}>{altOk ? '' : ''}</span> <h2 style={{ margin:0, fontSize:'clamp(20px,1.8vw,32px)', fontWeight:800, color:TEXT }}>
 {altOk ? 'KPI Status — Alt Grønt' : 'KPI Advarsler'}
 </h2> </div>
 {altOk ? (
 <motion.div
 initial={{ opacity:0, scale:0.95 }}
 animate={{ opacity:1, scale:1 }}
 transition={{ duration:0.5 }}
 style={{
 flex:1, display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center',
 background:'rgba(34,197,94,0.06)', border:'1px solid rgba(34,197,94,0.2)',
 borderRadius:20, padding:'48px 32px', textAlign:'center',
 }}
 > <motion.div
 animate={{ scale:[1, 1.05, 1] }}
 transition={{ duration:2, repeat:Infinity }}
 style={{ fontSize:'clamp(72px,7vw,140px)', marginBottom:24, lineHeight:1 }}
 > </motion.div> <div style={{ fontSize:'clamp(24px,2.2vw,40px)', fontWeight:800, color:OK, marginBottom:12 }}>
 Alle KPI'er i grønt
 </div> <div style={{ fontSize:'clamp(14px,1.2vw,20px)', color:SUBTEXT, maxWidth:500 }}>
 Alle 55 afdelinger på tværs af 13 divisioner kører inden for målsætningerne. Fortsæt det gode arbejde.
 </div> </motion.div>
 ) : (
 <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
 {kritiske.map((k, i) => (
 <motion.div key={i}
 initial={{ opacity:0, x:-20 }}
 animate={{ opacity:1, x:0 }}
 transition={{ delay: i * 0.06 }}
 style={{
 display:'flex', alignItems:'center', gap:14,
 background: k.status === 'kritisk' ? 'rgba(239,68,68,0.08)' : 'rgba(245,158,11,0.08)',
 border: `1px solid ${k.status === 'kritisk' ? 'rgba(239,68,68,0.25)' : 'rgba(245,158,11,0.25)'}`,
 borderRadius:12, padding:'14px 18px',
 borderLeft:`4px solid ${k.status === 'kritisk' ? DANGER : WARN}`,
 }}
 > <span style={{ fontSize:20 }}>{k.status === 'kritisk' ? '' : ''}</span> <div style={{ flex:1 }}> <div style={{ fontSize:14, fontWeight:700, color:TEXT }}>{k.afd_navn}</div> <div style={{ fontSize:12, color:SUBTEXT }}>
 {k.division_navn} · {k.kpi_navn}
 </div> </div> <div style={{ textAlign:'right' }}> <div style={{ fontSize:16, fontWeight:800, color: k.status === 'kritisk' ? DANGER : WARN }}>
 {k.vaerdi} {k.enhed}
 </div> <div style={{ fontSize:11, color:SUBTEXT }}>mål: {k.maal} {k.enhed}</div> </div> </motion.div>
 ))}
 </div>
 )}
 </div>
 );
}
// ─── HOVED-KOMPONENT ──────────────────────────────────────────────────────────
const SLIDES = [
 { id:'puls', komponent: SlideVirksomhedspuls, label:'Virksomhedspuls' },
 { id:'sundhed', komponent: SlideDivisionSundhed, label:'Division Sundhed' },
 { id:'leads', komponent: SlideLeadsPipeline, label:'Leads Pipeline' },
 { id:'sagsportal',komponent: SlideSagsportal, label:'Sagsportal Live' },
 { id:'kpi', komponent: SlideKpiStatus, label:'KPI Status' },
];
const SLIDE_SEC = 30;
export default function OSInfoskaerm({ inline = false }) {
 const [data, setData] = useState(null);
 const [trendData, setTrendData] = useState(null);
 const [nu, setNu] = useState(new Date());
 const [slideIdx, setSlideIdx] = useState(0);
 const [slideTimer, setSlideTimer] = useState(SLIDE_SEC);
 const [auto, setAuto] = useState(true);
 const hent = useCallback(async () => {
 try {
 const [d, t] = await Promise.allSettled([
 api('/os/firma/infoskaerm'),
 api('/os/firma/trend?koder=mrr,aktive_klienter'),
 ]);
 if (d.status === 'fulfilled') setData(d.value);
 if (t.status === 'fulfilled') setTrendData(t.value);
 } catch (err) {
 console.error('[OSInfoskaerm] hent fejl:', err);
 }
 }, []);
 useEffect(() => {
 hent();
 const iv = setInterval(hent, 60000);
 return () => clearInterval(iv);
 }, [hent]);
 // Klokke
 useEffect(() => {
 const iv = setInterval(() => setNu(new Date()), 1000);
 return () => clearInterval(iv);
 }, []);
 // Auto-slide timer
 useEffect(() => {
 if (!auto) return;
 const iv = setInterval(() => {
 setSlideTimer(t => {
 if (t <= 1) {
 setSlideIdx(s => (s + 1) % SLIDES.length);
 return SLIDE_SEC;
 }
 return t - 1;
 });
 }, 1000);
 return () => clearInterval(iv);
 }, [auto]);
 // Reset timer ved manuel navigation
 const naviger = (idx) => {
 setSlideIdx(idx);
 setSlideTimer(SLIDE_SEC);
 setAuto(false);
 setTimeout(() => setAuto(true), 10000);
 };
 // Udled drift-niveau: brug drift_status fra backend hvis tilgængeligt,
 // fald tilbage på den gamle kpi_kritiske-logik for bagudkompatibilitet
 const driftStatus = data?.drift_status;
 const driftNiveau = driftStatus
 ? driftStatus.niveau // 'ok' | 'advarsel' | 'kritisk'
 : (!data?.kpi_kritiske?.length ? 'ok' : 'advarsel'); // fallback: gammel logik
 const AktivSlide = SLIDES[slideIdx].komponent;
 const slideProps = {
 data, trendData,
 key: slideIdx,
 };
 const containerStyle = inline ? {
 width:'100%', minHeight:'80vh',
 background:`linear-gradient(180deg, ${BG} 0%, ${BG2} 100%)`,
 color:TEXT, fontFamily:FONT, borderRadius:16, padding:28, overflow:'hidden',
 } : {
 position:'fixed', inset:0,
 background:`linear-gradient(180deg, ${BG} 0%, ${BG2} 100%)`,
 color:TEXT, fontFamily:FONT, padding:'clamp(20px,2vw,40px)',
 overflow:'hidden', zIndex:999,
 };
 return (
 <div style={containerStyle}> <Header
 nu={nu}
 driftNiveau={driftNiveau}
 driftAarsager={driftStatus?.aarsager || []}
 slideIdx={slideIdx}
 totalSlides={SLIDES.length}
 slideTimer={slideTimer}
 SLIDE_SEC={SLIDE_SEC}
 />
 {/* Slide content */}
 <div style={{ flex:1, minHeight:0 }}> <AnimatePresence mode="wait"> <motion.div
 key={slideIdx}
 initial={{ opacity:0, y:30 }}
 animate={{ opacity:1, y:0 }}
 exit={{ opacity:0, y:-20 }}
 transition={{ duration:0.4 }}
 > <AktivSlide {...slideProps} /> </motion.div> </AnimatePresence> </div>
 {/* Navigation bund */}
 <div style={{
 position: inline ? 'relative' : 'fixed',
 bottom: inline ? 'auto' : 20, left:0, right:0,
 display:'flex', justifyContent:'center', gap:12,
 paddingTop: inline ? 24 : 0,
 flexWrap:'wrap',
 }}>
 {SLIDES.map((s, i) => (
 <button
 key={s.id}
 onClick={() => naviger(i)}
 style={{
 background: i === slideIdx ? `rgba(63,212,199,0.15)` : 'rgba(255,255,255,0.04)',
 border: `1px solid ${i === slideIdx ? ACCENT : BORDER}`,
 color: i === slideIdx ? ACCENT : SUBTEXT,
 borderRadius:20, padding:'6px 16px', fontSize:12, cursor:'pointer',
 fontWeight: i === slideIdx ? 700 : 400,
 transition:'all .2s',
 }}
 >
 {s.label}
 </button>
 ))}
 {!inline && (
 <button
 onClick={() => window.close()}
 style={{
 background:'rgba(239,68,68,0.1)', border:`1px solid rgba(239,68,68,0.2)`,
 color:DANGER, borderRadius:20, padding:'6px 16px', fontSize:12, cursor:'pointer',
 }}
 >
 Luk
 </button>
 )}
 </div> </div>
 );
}